import { useState } from 'react'
import type { PipelineCard } from '@1130-studio/shared'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'

interface TransitionModalProps {
  card: PipelineCard | null
  isOpen: boolean
  onClose: () => void
  onConfirm: (projectName: string) => void
  isLoading?: boolean
}

export function TransitionModal({
  card,
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: TransitionModalProps) {
  const [projectName, setProjectName] = useState('')

  if (!isOpen || !card) return null

  const defaultName = `Проект: ${card.name}`

  const handleConfirm = () => {
    onConfirm(projectName || defaultName)
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[110] p-4">
      <div className="glass-card w-full max-w-md rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <h2 className="text-lg font-bold text-white">
            Создать проект из заявки
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Заявка будет конвертирована в проект
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Project Name */}
          <Input
            label="Название проекта"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder={defaultName}
          />

          {/* Client Preview */}
          <div>
            <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-3">
              Будет создан клиент
            </label>
            <div className="bg-white/5 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-lime/10 flex items-center justify-center text-accent-lime font-semibold">
                  {card.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-white">{card.name}</div>
                  <div className="text-xs text-text-secondary">Новый клиент</div>
                </div>
              </div>
              <div className="pt-2 border-t border-white/10 space-y-1 text-sm text-text-secondary">
                {card.email && (
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    {card.email}
                  </div>
                )}
                {card.phone && (
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    {card.phone}
                  </div>
                )}
                {card.telegram && (
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 opacity-60" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.121.099.155.232.17.324.015.092.034.303.019.467z"/>
                    </svg>
                    {card.telegram}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-white/10">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={onClose}
            disabled={isLoading}
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleConfirm}
            loading={isLoading}
          >
            Создать проект
          </Button>
        </div>
      </div>
    </div>
  )
}
